import { useCallback, useEffect, useState } from 'react';
import logger from '../../../utils/logger';
import {
  YOUTUBE_URL_REGEX,
  extractSongDetails,
  extractVideoId,
  fetchVideoDetails,
  normalizeYouTubeURL,
} from '../utils/youtube';

/**
 * Owns the entire "paste a YouTube URL" interaction:
 *
 *   - `songURL` + validation flags (`isUrlValid`, `showFullForm`,
 *     `urlError`).
 *   - Fetched metadata (`fetchedTitle`, `fetchedThumbnail`) + derived
 *     song name / artist handed out via callback-style setters.
 *   - Three input event handlers: `onChange`, `onPaste` (format-guards
 *     the clipboard value before applying), and `onKeyDown` (commits
 *     on Enter).
 *   - A re-fetch effect that refreshes the thumbnail when the URL is
 *     edited mid-post (only in `isEditing` mode).
 *
 * `onSongDetails` is called whenever a successful YouTube fetch yields
 * a name/artist pair; the parent owns the actual `songName`/`songArtist`
 * state so the user can still freely edit them.
 *
 * `initialState` seeds the hook when a post is loaded for editing.
 */
const useYouTubeUrlInput = ({
  isMounted,
  isEditing,
  postToEdit,
  onSongDetails,
  onClearUrlError,
  initialState = null,
}) => {
  const [songURL, setSongURL] = useState(initialState?.songURL ?? '');
  const [isUrlValid, setIsUrlValid] = useState(initialState?.isUrlValid ?? false);
  const [showFullForm, setShowFullForm] = useState(
    initialState?.showFullForm ?? false
  );
  const [urlError, setUrlError] = useState('');
  const [fetchedTitle, setFetchedTitle] = useState(
    initialState?.fetchedTitle ?? ''
  );
  const [fetchedThumbnail, setFetchedThumbnail] = useState(
    initialState?.fetchedThumbnail ?? ''
  );

  const clearUrlInput = useCallback(() => {
    setSongURL('');
    setIsUrlValid(false);
    setShowFullForm(false);
  }, []);

  const handleUrlChange = useCallback(
    async (e) => {
      if (!isMounted()) return;

      let nextUrl = e.target.value;
      setSongURL(nextUrl);
      setUrlError('');

      if (onClearUrlError) onClearUrlError();

      if (!nextUrl.trim()) {
        if (isMounted()) {
          setIsUrlValid(false);
          setShowFullForm(false);
        }
        return;
      }

      if (!YOUTUBE_URL_REGEX.test(nextUrl)) {
        if (isMounted()) {
          setIsUrlValid(false);
          setShowFullForm(false);
          setUrlError('Please enter a valid YouTube URL');
        }
        return;
      }

      try {
        if (nextUrl.includes('youtube.com') || nextUrl.includes('youtu.be')) {
          if (nextUrl.includes('music.youtube.com')) {
            nextUrl = normalizeYouTubeURL(nextUrl);
            setSongURL(nextUrl);
          }

          if (YOUTUBE_URL_REGEX.test(nextUrl)) {
            if (isMounted()) setIsUrlValid(true);

            try {
              const videoId = extractVideoId(nextUrl);
              if (videoId) {
                let videoDetails = null;
                try {
                  videoDetails = await fetchVideoDetails(videoId);
                } catch (error) {
                  logger.error('fetchVideoDetails threw:', error);
                  if (isMounted()) {
                    setIsUrlValid(false);
                    setShowFullForm(false);
                  }
                  return;
                }

                if (videoDetails && videoDetails.snippet && isMounted()) {
                  const { title, thumbnails, channelTitle } =
                    videoDetails.snippet;
                  const extractedDetails = extractSongDetails(
                    channelTitle,
                    title
                  );
                  if (onSongDetails) onSongDetails(extractedDetails);
                  setFetchedTitle(title);
                  setFetchedThumbnail(thumbnails);
                  setShowFullForm(true);
                } else if (isMounted()) {
                  setIsUrlValid(false);
                  setShowFullForm(false);
                }
              }
            } catch (error) {
              logger.warn('Error processing video details:', error);
              if (isMounted()) {
                setIsUrlValid(false);
                setShowFullForm(false);
              }
            }
          } else if (isMounted()) {
            setIsUrlValid(false);
            setShowFullForm(false);
          }
        } else if (isMounted()) {
          setIsUrlValid(false);
          setShowFullForm(false);
        }
      } catch (error) {
        logger.warn('Error in handleUrlChange:', error);
        if (isMounted()) {
          setIsUrlValid(false);
          setShowFullForm(false);
        }
      }
    },
    [isMounted, onSongDetails, onClearUrlError]
  );

  const handleUrlPaste = useCallback(
    async (e) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');

      if (!YOUTUBE_URL_REGEX.test(pastedText)) {
        setUrlError('Whoops! Please paste a valid YouTube URL');
        return;
      }

      setUrlError('');
      const input = e.target;
      input.value = pastedText;
      await handleUrlChange({ target: input });
    },
    [handleUrlChange]
  );

  const handleUrlKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === 'Return') {
        e.preventDefault();
        handleUrlChange(e);
      }
    },
    [handleUrlChange]
  );

  /*
    In edit mode, when the user swaps in a different YouTube URL than the
    original, transparently refresh the thumbnail + title so the preview
    matches the new target. We intentionally only fire when
    `songURL !== postToEdit.URL` to avoid redundant API calls on reload.
  */
  useEffect(() => {
    const refetchThumbnailIfNeeded = async () => {
      if (isEditing && postToEdit && songURL && songURL !== postToEdit.URL) {
        logger.debug('URL changed during edit, re-fetching thumbnail');
        const videoId = extractVideoId(songURL);
        if (videoId) {
          try {
            const videoDetails = await fetchVideoDetails(videoId);
            if (videoDetails && videoDetails.snippet && isMounted()) {
              const { title, thumbnails, channelTitle } = videoDetails.snippet;
              const extractedDetails = extractSongDetails(channelTitle, title);
              if (onSongDetails) onSongDetails(extractedDetails);
              setFetchedTitle(title);
              setFetchedThumbnail(thumbnails);
              logger.debug('Successfully re-fetched thumbnail for edited URL');
            }
          } catch (error) {
            logger.error('Error re-fetching thumbnail during edit:', error);
          }
        }
      }
    };

    refetchThumbnailIfNeeded();
  }, [songURL, isEditing, postToEdit, isMounted, onSongDetails]);

  return {
    songURL,
    setSongURL,
    isUrlValid,
    setIsUrlValid,
    showFullForm,
    setShowFullForm,
    urlError,
    setUrlError,
    fetchedTitle,
    setFetchedTitle,
    fetchedThumbnail,
    setFetchedThumbnail,
    clearUrlInput,
    handleUrlChange,
    handleUrlPaste,
    handleUrlKeyDown,
  };
};

export default useYouTubeUrlInput;
